import { useManifest } from '@/contexts/ManifestContext';
import { cn } from '@/utils/cn';
import React from 'react';
import { Link, useLocation } from 'wouter-preact';

interface ProjectCardProps {
  children: React.ReactNode;
  href: string;
  className?: string;
  ariaLabel?: string;
  target?: string;
  onClick?: () => void;
  /** Skip the forced scroll-to-top so the browser's native per-history-entry
   * scroll position is preserved for the page being left (e.g. so pressing
   * Back lands where the user was, instead of at the top). The destination
   * page is responsible for scrolling itself to top on mount if it needs to. */
  preserveScroll?: boolean;
}

export const AppLink = ({
  className,
  href,
  children,
  ariaLabel,
  target,
  onClick,
  preserveScroll = false,
}: ProjectCardProps) => {
  const [location] = useLocation();
  const { locale } = useManifest();

  if (/^https?:\/\//.test(href)) {
    return (
      <a
        href={href}
        target={target}
        className={cn('', className)}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  function constructLocalizedUrl(path: string): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const segments = location.split('/').filter((segment) => segment !== '');
    const isValidLocale = segments[0] && locale.supported.includes(segments[0]);
    const language = isValidLocale ? `/${segments[0]}` : '';
    return `${language}${cleanPath}`;
  }

  const handleNavClick = () => {
    if (!preserveScroll) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    onClick?.();
  };

  return (
    <Link
      className={cn('', className)}
      href={constructLocalizedUrl(href)}
      aria-label={ariaLabel}
      onClick={handleNavClick}
    >
      {children}
    </Link>
  );
};
